import { router } from "@inertiajs/react"
import { Check, ChevronsUpDown, Loader2, Star } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { csrfFetch } from "@/lib/csrf-fetch"
import { cn } from "@/lib/utils"
import type {
  ReportViewCreator,
  ReportViewData,
  ReportViewIssue,
} from "@/types"

export type SavedView = ReportViewData

export function reportViewIssueMessage(issue: ReportViewIssue) {
  return typeof issue === "string" ? issue : issue.message
}

export function ViewCreatorSubtext({
  creator,
}: {
  creator?: ReportViewCreator | null
}) {
  const displayName = creator?.display_name.trim()
  if (!displayName) return null

  const text = `Created by ${displayName}`
  return (
    <span
      className="text-muted-foreground min-w-0 truncate text-xs"
      title={text}
    >
      {text}
    </span>
  )
}

interface Props {
  views: SavedView[]
  activeView: SavedView | null
  reportPath: string
  canCreate: boolean
  hasUnsavedChanges?: boolean
  canSaveCurrentView?: boolean
  saveDisabledReason?: string
  onSaveCurrentView: () => void
}

interface ViewsResponse {
  items?: SavedView[]
  views?: SavedView[]
  pagination?: {
    page: number
    per_page?: number
    total?: number
    total_pages?: number
    pages?: number
  }
}

const VIEW_PAGE_SIZE = 25

export function viewSelectionUrl(reportPath: string, viewId: number | null) {
  return `${reportPath}?view_id=${viewId ?? "default"}&remember_view=1`
}

export function viewDiscardConfirmation(sourceName: string) {
  return `Discard changes marked Modified from “${sourceName}” and switch views?`
}

export function ViewPicker({
  views,
  activeView,
  reportPath,
  canCreate,
  hasUnsavedChanges = false,
  canSaveCurrentView = true,
  saveDisabledReason,
  onSaveCurrentView,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [items, setItems] = useState(views)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(views.length >= VIEW_PAGE_SIZE)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestRef = useRef(0)

  useEffect(() => {
    if (query.trim().length > 0) return

    setItems(views)
    setPage(1)
    setHasMore(views.length >= VIEW_PAGE_SIZE)
  }, [query, views])

  useEffect(() => {
    if (!open || query.trim().length === 0) {
      requestRef.current += 1
      setItems(views)
      setPage(1)
      setHasMore(views.length >= VIEW_PAGE_SIZE)
      setLoading(false)
      setLoadingMore(false)
      setError(null)
      return
    }

    const requestId = ++requestRef.current
    // A query change invalidates an in-flight load-more request. Clear its
    // spinner here because that superseded request's finally block is ignored.
    setLoadingMore(false)
    const timeout = window.setTimeout(async () => {
      setLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({ q: query.trim(), page: "1" })
        const response = await csrfFetch(`${reportPath}/views?${params}`, {
          headers: { Accept: "application/json" },
        })
        const body = (await response.json().catch(() => ({}))) as ViewsResponse
        if (requestId !== requestRef.current) return
        if (!response.ok) throw new Error("Could not search views.")
        const nextItems = body.items ?? body.views ?? []
        const responsePage = body.pagination?.page ?? 1
        const totalPages =
          body.pagination?.total_pages ?? body.pagination?.pages ?? null
        setItems(nextItems)
        setPage(responsePage)
        setHasMore(
          totalPages === null
            ? nextItems.length >= VIEW_PAGE_SIZE
            : responsePage < totalPages,
        )
      } catch (reason) {
        if (requestId !== requestRef.current) return
        setError(
          reason instanceof Error ? reason.message : "Could not search views.",
        )
      } finally {
        if (requestId === requestRef.current) setLoading(false)
      }
    }, 250)

    return () => window.clearTimeout(timeout)
  }, [open, query, reportPath, views])

  const loadMore = async () => {
    if (loading || loadingMore || !hasMore) return

    const requestId = ++requestRef.current
    const nextPage = page + 1
    setLoadingMore(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(nextPage) })
      if (query.trim()) params.set("q", query.trim())
      const response = await csrfFetch(`${reportPath}/views?${params}`, {
        headers: { Accept: "application/json" },
      })
      const body = (await response.json().catch(() => ({}))) as ViewsResponse
      if (requestId !== requestRef.current) return
      if (!response.ok) throw new Error("Could not load more views.")

      const nextItems = body.items ?? body.views ?? []
      const responsePage = body.pagination?.page ?? nextPage
      const totalPages =
        body.pagination?.total_pages ?? body.pagination?.pages ?? null
      setItems((current) => {
        const seen = new Set(current.map((view) => view.id))
        return [...current, ...nextItems.filter((view) => !seen.has(view.id))]
      })
      setPage(responsePage)
      setHasMore(
        totalPages === null
          ? nextItems.length >= VIEW_PAGE_SIZE
          : responsePage < totalPages,
      )
    } catch (reason) {
      if (requestId !== requestRef.current) return
      setError(
        reason instanceof Error ? reason.message : "Could not load more views.",
      )
    } finally {
      if (requestId === requestRef.current) setLoadingMore(false)
    }
  }

  const selectView = (view: SavedView | null) => {
    const sourceName = activeView?.name ?? "Default"
    if (
      hasUnsavedChanges &&
      !window.confirm(viewDiscardConfirmation(sourceName))
    ) {
      return
    }
    setOpen(false)
    router.visit(viewSelectionUrl(reportPath, view?.id ?? null), {
      preserveScroll: true,
    })
  }

  const triggerLabel = activeView?.name ?? "Default"

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setQuery("")
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={`Current report view: ${triggerLabel}`}
          className="h-11 min-w-0 justify-between sm:min-w-56"
        >
          <span className="max-w-[min(15rem,55vw)] truncate">
            {triggerLabel}
          </span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(22rem,calc(100vw-2rem))] p-0"
      >
        <Command
          shouldFilter={false}
          className="**:data-[slot=command-input-wrapper]:h-11"
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search organization views…"
            aria-label="Search organization views"
          />
          <CommandList className="max-h-80" aria-busy={loading || loadingMore}>
            <CommandGroup heading="Report defaults">
              <CommandItem
                value="default"
                onSelect={() => selectView(null)}
                className="min-h-11"
              >
                <Star className="mr-2 size-4" />
                <span className="flex-1">Default</span>
                {!activeView && <Check className="size-4" aria-hidden />}
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Organization views">
              {loading && (
                <div
                  className="text-muted-foreground flex min-h-11 items-center gap-2 px-3 py-4 text-sm"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="size-4 animate-spin" />
                  Searching…
                </div>
              )}
              {!loading && error && (
                <div
                  role="alert"
                  className="text-destructive px-3 py-4 text-sm"
                >
                  {error}
                </div>
              )}
              {!loading && !error && items.length === 0 && (
                <CommandEmpty>
                  {query
                    ? "No organization views match your search."
                    : "No organization views yet."}
                </CommandEmpty>
              )}
              {!loading &&
                items.map((view) => (
                  <CommandItem
                    key={view.id}
                    value={String(view.id)}
                    onSelect={() => selectView(view)}
                    className="min-h-11"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{view.name}</span>
                      <span className="flex min-w-0 items-center gap-2">
                        <ViewCreatorSubtext creator={view.creator} />
                        {view.status === "needs_review" && (
                          <span className="shrink-0 text-xs text-amber-700">
                            Needs review
                          </span>
                        )}
                      </span>
                    </span>
                    <Check
                      className={cn(
                        "size-4",
                        activeView?.id === view.id
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                      aria-hidden
                    />
                  </CommandItem>
                ))}
              {!loading && !error && hasMore && (
                <div className="p-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 w-full"
                    disabled={loadingMore}
                    onClick={() => void loadMore()}
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="mr-2 size-4 animate-spin" />
                        Loading more views…
                      </>
                    ) : (
                      "Load more views"
                    )}
                  </Button>
                </div>
              )}
              {loadingMore && (
                <span className="sr-only" role="status" aria-live="polite">
                  Loading more organization views.
                </span>
              )}
            </CommandGroup>
          </CommandList>
          {canCreate && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                className="h-11 w-full justify-start"
                disabled={!canSaveCurrentView}
                aria-describedby={
                  !canSaveCurrentView && saveDisabledReason
                    ? "view-save-disabled-reason"
                    : undefined
                }
                onClick={() => {
                  setOpen(false)
                  onSaveCurrentView()
                }}
              >
                + Save as organization view
              </Button>
              {!canSaveCurrentView && saveDisabledReason && (
                <p
                  id="view-save-disabled-reason"
                  className="text-muted-foreground px-3 py-2 text-xs"
                >
                  {saveDisabledReason}
                </p>
              )}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
